import { Link } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { ArrowRight } from 'lucide-react';

export const CaseStudies = () => {
  return (
    <div className="min-h-screen bg-[#0a0a0a] text-[#F5F5F5]">
      <section className="container mx-auto px-6 py-20 max-w-6xl">
        <h1 className="text-5xl font-bold mb-16">Case studies</h1>

        {/* Featured Case Study 1 - TukarBateri */}
        <Card className="bg-[#1a1a1a] border-none border-t-4 border-t-[#E85D04] p-8 space-y-6">
          <div className="flex justify-between items-start">
            <div>
              <h2 className="text-2xl font-bold text-[#F5F5F5]">TukarBateri.com</h2>
              <p className="text-[#888888] mt-1">Car battery DTC</p>
            </div>
            <Badge className="bg-[#E85D04] text-white hover:bg-[#E85D04]/90 px-4 py-2 text-sm">
              9x ROAS · Google Ads
            </Badge>
          </div>
          
          <div className="border-l-4 border-l-[#E85D04] pl-6 py-2">
            <p className="text-[#F5F5F5] leading-relaxed">
              <span className="font-semibold">The plateau:</span> Organic traffic was decent but CVR was stuck at 5%. Every ringgit spent on ads was amplifying a leaky funnel.
            </p>
          </div>

          <div className="border-l-4 border-l-[#22c55e] pl-6 py-2">
            <p className="text-[#F5F5F5] leading-relaxed">
              <span className="font-semibold">The fix:</span> 4 sequential experiments on existing traffic before touching paid spend. CVR moved from 5% to 17%. Revenue scaled from $30k to $100k in 5 months.
            </p>
          </div>

          <ul className="space-y-3 text-[#888888] pl-6">
            <li>· WhatsApp vs call-only CTA — +30% CTA clicks in 7 days</li>
            <li>· Headline rewritten to reflect USP — +100% avg sessions, +20% WhatsApp clicks</li>
            <li>· Car model imagery vs brand logos — +15% brand-related clicks</li>
            <li>· Day/night shift pricing to aid decisions — +30% revenue per click, −10 min avg conversation</li>
          </ul>

          <div className="flex flex-wrap gap-2 pt-4">
            {['Convert', 'Google Ads', 'Hotjar', 'Mouseflow', 'Airtable', 'GTM', 'Looker Studio', 'HubSpot'].map((tool) => (
              <Badge key={tool} variant="outline" className="bg-[#0a0a0a] border-[#888888] text-[#888888] px-3 py-1">
                {tool}
              </Badge>
            ))}
          </div>
        </Card>

        {/* Case Study 2 - iGaming Platform */}
        <Card className="bg-[#1a1a1a] border-none border-t-4 border-t-[#E85D04] p-8 space-y-6 mt-8">
          <div className="flex justify-between items-start">
            <div>
              <h2 className="text-2xl font-bold text-[#F5F5F5]">iGaming Platform</h2>
              <p className="text-[#888888] mt-1">Regulated Market · Identity Undisclosed</p>
            </div>
            <Badge className="bg-[#E85D04] text-white hover:bg-[#E85D04]/90 px-4 py-2 text-sm">
              30x deposit growth · Meta Ads
            </Badge>
          </div>
          
          <div className="border-l-4 border-l-[#E85D04] pl-6 py-2">
            <p className="text-[#F5F5F5] leading-relaxed">
              <span className="font-semibold">The plateau:</span> $60k–$100k USD in monthly ad spend with daily deposits stuck at $30k. Spend was scaling. Deposits weren't. A classic conversion architecture problem in one of the hardest environments to optimise — restricted retargeting, compliance-limited creative, and a high-friction registration-to-deposit funnel.
            </p>
          </div>

          <div className="border-l-4 border-l-[#22c55e] pl-6 py-2">
            <p className="text-[#F5F5F5] leading-relaxed">
              <span className="font-semibold">The fix:</span> Built an automated creative strategist-to-CRO feedback loop — ad-level data directly informed landing page test priorities, and CRO learnings fed back into creative briefs. No more siloed functions. Within 5 months, daily deposits scaled from $30k to $200k.
            </p>
          </div>

          <ul className="space-y-3 text-[#888888] pl-6">
            <li>· Automated creative-to-CRO loop eliminated lag between ad performance signals and funnel response</li>
            <li>· Meta creative strategy rebuilt around deposit-intent audience segmentation</li>
            <li>· Landing page test velocity accelerated by removing IT dependency — tests coded directly in HTML/CSS/JS</li>
            <li>· Full funnel unified under one diagnostic framework — from ad click to completed deposit</li>
          </ul>

          <div className="flex flex-wrap gap-2 pt-4">
            {['Meta Ads Manager', 'Convert', 'Hotjar', 'Airtable', 'Looker Studio', 'GTM', 'Miro'].map((tool) => (
              <Badge key={tool} variant="outline" className="bg-[#0a0a0a] border-[#888888] text-[#888888] px-3 py-1">
                {tool}
              </Badge>
            ))}
          </div>

          <p className="text-[#888888] text-sm italic pt-2">
            Client identity withheld by NDA. Results verified internally.
          </p>
        </Card>

        {/* Case Study 3 - Puffy */}
        <Card className="bg-[#1a1a1a] border-none border-t-4 border-t-[#E85D04] p-8 space-y-6 mt-8">
          <div className="flex justify-between items-start">
            <div>
              <h2 className="text-2xl font-bold text-[#F5F5F5]">Puffy</h2>
              <p className="text-[#888888] mt-1">DTC Mattress Brand</p>
            </div>
            <Badge className="bg-[#E85D04] text-white hover:bg-[#E85D04]/90 px-4 py-2 text-sm">
              +$20M ARR · Pure CRO
            </Badge>
          </div>
          
          <div className="border-l-4 border-l-[#E85D04] pl-6 py-2">
            <p className="text-[#F5F5F5] leading-relaxed">
              <span className="font-semibold">The plateau:</span> High traffic, serious ad spend, but blended CVR across mattress PDPs had plateaued. The product was strong. The funnel wasn't converting at the rate the traffic volume deserved.
            </p>
          </div>

          <div className="border-l-4 border-l-[#22c55e] pl-6 py-2">
            <p className="text-[#F5F5F5] leading-relaxed">
              <span className="font-semibold">The fix:</span> Pure CRO engagement — no paid media, no creative production. Rebuilt the test prioritisation framework, introduced AI-assisted processes to increase experiment velocity, and ran a structured optimisation programme across multiple mattress PDPs. Blended CVR lifted by 5%, resulting in a $20M USD lift in ARR. Test velocity increased by 30%.
            </p>
          </div>

          <ul className="space-y-3 text-[#888888] pl-6">
            <li>· 5% blended CVR lift across multiple mattress product detail pages</li>
            <li>· $20M USD incremental ARR from conversion improvements alone — zero increase in ad spend</li>
            <li>· 30% increase in CRO test velocity through AI-assisted experiment planning and QA processes</li>
            <li>· Structured test prioritisation framework installed — ranked experiments by revenue impact, not ease</li>
          </ul>

          <div className="flex flex-wrap gap-2 pt-4">
            {['VWO', 'Growthbook', 'Hotjar', 'Mouseflow', 'Airtable', 'Looker Studio', 'GTM', 'Claude Cowork', 'Miro'].map((tool) => (
              <Badge key={tool} variant="outline" className="bg-[#0a0a0a] border-[#888888] text-[#888888] px-3 py-1">
                {tool}
              </Badge>
            ))}
          </div>
        </Card>

        {/* Placeholder for more case studies */}
        <Card className="bg-transparent border-2 border-dashed border-[#E85D04] p-12 text-center mt-8">
          <p className="text-[#888888] text-lg">More case studies coming soon — currently under NDA</p>
        </Card>

        {/* CTA Section */}
        <div className="text-center mt-20">
          <Link to="/contact">
            <Button className="bg-[#E85D04] hover:bg-[#E85D04]/90 text-white px-8 py-6 text-lg font-semibold transition-colors group">
              Work with me
              <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};